import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Trophy, Heart, Play, Rocket, Sparkles, Loader2, X, ArrowLeft } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Spinner, EmptyState } from "@/components/Bits";
import Logo from "@/components/Logo";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const MEDAL = { 1: "🥇", 2: "🥈", 3: "🥉" };

export default function BuildChallenge() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const inApp = location.pathname.startsWith("/app");
  const [data, setData] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [enterOpen, setEnterOpen] = useState(false);
  const [myGames, setMyGames] = useState(null);
  const [entering, setEntering] = useState(false);

  const load = () => api.get("/build-challenge").then((r) => setData(r.data)).catch(() => setData(false));
  useEffect(() => { load(); }, []);

  const playHref = (gid) => (user ? `/app/arcade/${gid}` : `/g/${gid}`);

  const vote = async (e) => {
    if (!user) { toast("Sign in to vote for your favourites!"); navigate("/register"); return; }
    setBusyId(e.entry_id);
    setData((d) => ({ ...d, entries: d.entries.map((x) => x.entry_id === e.entry_id
      ? { ...x, voted: !x.voted, vote_count: x.vote_count + (x.voted ? -1 : 1) } : x) }));
    try { await api.post(`/build-challenge/${e.entry_id}/vote`); }
    catch { load(); }
    finally { setBusyId(null); }
  };

  const openEnter = async () => {
    if (!user) { navigate("/register"); return; }
    setEnterOpen(true); setMyGames(null);
    try { const { data } = await api.get("/build-challenge/my-games"); setMyGames(data.games || []); }
    catch { setMyGames([]); }
  };

  const enterGame = async (gid) => {
    setEntering(true);
    try {
      const { data } = await api.post("/build-challenge/enter", { game_id: gid });
      toast.success(data.message || "Entered! 🏆");
      setEnterOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't enter that game."); }
    finally { setEntering(false); }
  };

  const withdraw = async (e) => {
    if (!window.confirm("Withdraw this game from the challenge?")) return;
    try { await api.delete(`/build-challenge/${e.entry_id}`); toast.success("Withdrawn."); load(); }
    catch { toast.error("Couldn't withdraw."); }
  };

  const body = () => {
    if (!data) return <Spinner />;
    if (data === false) return <EmptyState emoji="🏁" title="Challenge unavailable" text="Couldn't load the challenge right now." />;
    const ch = data.challenge;
    return (
      <>
        <div className="rounded-3xl border border-[#3b9eff]/30 bg-gradient-to-br from-[#7c3aed]/[0.12] via-[#3b9eff]/[0.06] to-transparent p-6 sm:p-8 mb-8" data-testid="challenge-hero">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#8fc6ff] mb-2">
            <Rocket className="w-4 h-4" /> Community Challenge
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold">{ch.title}</h1>
          <p className="text-[hsl(var(--muted-foreground))] mt-2 max-w-2xl">{ch.tagline}</p>
          <div className="mt-4 rounded-2xl bg-black/20 border border-white/10 p-4 max-w-2xl">
            <div className="text-[11px] font-bold uppercase tracking-wide text-amber-400 mb-1">This season's theme</div>
            <p className="text-sm">{ch.prompt}</p>
          </div>
          <div className="mt-5 flex flex-wrap gap-3">
            <Button onClick={openEnter} className="rounded-full bg-gradient-to-r from-[#7c3aed] to-[#3b9eff] font-bold" data-testid="challenge-enter-btn">
              <Sparkles className="w-4 h-4" /> Enter my game
            </Button>
            <Button variant="outline" className="rounded-full" onClick={() => navigate(user ? "/app/create" : "/register")} data-testid="challenge-create-btn">
              Build something new
            </Button>
          </div>
        </div>

        <div className="flex items-center gap-2 mb-4">
          <Trophy className="w-5 h-5 text-amber-400" />
          <h2 className="font-display text-2xl font-extrabold">The gallery</h2>
          <span className="text-sm text-[hsl(var(--muted-foreground))]">{data.count} {data.count === 1 ? "entry" : "entries"}</span>
        </div>

        {data.entries.length === 0 ? (
          <EmptyState emoji="🎨" title="Be the first to enter!" text="No games in the challenge yet — publish a game and enter it to kick things off." />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="challenge-gallery">
            {data.entries.map((e) => (
              <div key={e.entry_id} data-testid={`challenge-entry-${e.entry_id}`}
                className={`relative rounded-2xl border overflow-hidden bg-[hsl(var(--card))] card-hover ${e.rank <= 3 ? "border-amber-400/40" : "border-[hsl(var(--border))]"}`}>
                <button onClick={() => navigate(playHref(e.game_id))} className="block w-full text-left">
                  <div className="relative h-36 bg-gradient-to-br from-[#1f6feb]/20 to-[#7c3aed]/25 grid place-items-center">
                    {e.cover_image ? <img src={`${BACKEND}${e.cover_image}`} alt="" className="w-full h-full object-cover" /> : <span className="text-5xl">{e.cover_emoji}</span>}
                    {e.rank <= 3 && <span className="absolute top-2 left-2 text-2xl drop-shadow">{MEDAL[e.rank]}</span>}
                    <span className="absolute bottom-2 right-2 inline-flex items-center gap-1 text-[11px] font-semibold bg-black/50 text-white rounded-full px-2 py-0.5"><Play className="w-3 h-3" /> {e.plays}</span>
                  </div>
                </button>
                <div className="p-3">
                  <div className="font-bold truncate">{e.title}</div>
                  <button onClick={() => navigate(`/app/creators/${e.creator_id}`)} className="text-[12px] text-[hsl(var(--muted-foreground))] hover:underline truncate block">by {e.creator_name}</button>
                  <div className="mt-2.5 flex items-center gap-2">
                    <Button size="sm" variant={e.voted ? "default" : "outline"} disabled={busyId === e.entry_id}
                      onClick={() => vote(e)} data-testid={`challenge-vote-${e.entry_id}`}
                      className={`rounded-full h-8 flex-1 ${e.voted ? "bg-pink-500 hover:bg-pink-600 border-pink-500" : ""}`}>
                      <Heart className={`w-4 h-4 ${e.voted ? "fill-white" : ""}`} /> {e.vote_count}
                    </Button>
                    <Button size="sm" className="rounded-full h-8" onClick={() => navigate(playHref(e.game_id))} data-testid={`challenge-play-${e.entry_id}`}>
                      <Play className="w-4 h-4" /> Play
                    </Button>
                    {e.is_mine && (
                      <button onClick={() => withdraw(e)} title="Withdraw" data-testid={`challenge-withdraw-${e.entry_id}`}
                        className="w-8 h-8 shrink-0 rounded-full grid place-items-center text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--secondary))]"><X className="w-4 h-4" /></button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <Dialog open={enterOpen} onOpenChange={setEnterOpen}>
          <DialogContent data-testid="challenge-enter-dialog">
            <DialogHeader>
              <DialogTitle>Enter a game in the challenge</DialogTitle>
              <DialogDescription>Pick one of your published games to add to the "{ch.title}" gallery.</DialogDescription>
            </DialogHeader>
            {!myGames ? <div className="py-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div> :
              myGames.length === 0 ? (
                <div className="text-center py-6">
                  <p className="text-sm text-[hsl(var(--muted-foreground))] mb-4">You don't have a published game yet. Publish one to the Arcade first!</p>
                  <Button onClick={() => { setEnterOpen(false); navigate("/app/create"); }} className="rounded-full" data-testid="challenge-enter-create">Create a game</Button>
                </div>
              ) : (
                <div className="space-y-2 max-h-[52vh] overflow-y-auto">
                  {myGames.map((g) => (
                    <div key={g.id} className="flex items-center gap-3 rounded-xl border border-[hsl(var(--border))] p-2.5" data-testid={`challenge-mygame-${g.id}`}>
                      <span className="text-2xl">{g.cover_emoji}</span>
                      <span className="font-semibold truncate flex-1">{g.title}</span>
                      {g.entered ? <span className="text-xs font-semibold text-emerald-500 shrink-0">✓ Entered</span> :
                        <Button size="sm" disabled={entering} onClick={() => enterGame(g.id)} className="rounded-full h-8 shrink-0" data-testid={`challenge-enter-game-${g.id}`}>
                          {entering ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enter"}
                        </Button>}
                    </div>
                  ))}
                </div>
              )}
          </DialogContent>
        </Dialog>
      </>
    );
  };

  if (inApp) {
    return <div className="p-5 lg:p-8 max-w-6xl mx-auto" data-testid="build-challenge-page">{body()}</div>;
  }

  return (
    <div className="min-h-screen bg-[#05060a] text-[hsl(var(--foreground))]" data-testid="build-challenge-page">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-black/40 border-b border-white/5">
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
          <Logo to="/" size="md" animated />
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => navigate("/")} data-testid="challenge-home"><ArrowLeft className="w-4 h-4" /> Home</Button>
            {!user && <Button size="sm" className="rounded-full bg-gradient-to-r from-[#1f6feb] to-[#3b9eff]" onClick={() => navigate("/register")} data-testid="challenge-signup">Start creating</Button>}
          </div>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-5 py-8">{body()}</main>
    </div>
  );
}
